import { useState, useCallback, useRef } from 'react'
import {
  budgetCategories,
  goals,
  investments,
  transactions,
} from '../data/mockData'

export type MessageType =
  | 'text'
  | 'budget_breakdown'
  | 'goal_progress'
  | 'investment_summary'
  | 'transaction_list'
  | 'insight_card'
  | 'spending_chart'

export interface RichData {
  type: MessageType
  payload?: any
}

export interface Message {
  id: string
  role: 'user' | 'assistant'
  content: string
  richData?: RichData
  timestamp: Date
  isStreaming?: boolean
}

const SUGGESTED_PROMPTS = [
  'How much did I spend on food this month?',
  'Am I on track with my savings goals?',
  'Analyze my investment portfolio',
  "What's my credit score trend?",
  'Show me my biggest expenses',
  'How can I reduce my spending?',
]

function generateResponse(query: string): {
  text: string
  richData?: RichData
} {
  const q = query.toLowerCase()

  if (
    q.includes('food') ||
    q.includes('dining') ||
    q.includes('restaurant') ||
    q.includes('grocery')
  ) {
    const foodBudget = budgetCategories.find((b) => b.name === 'Food & Dining')!
    return {
      text: `You've spent **$${foodBudget.spent.toFixed(2)}** on food & dining this month out of your **$${foodBudget.limit}** budget. That's ${Math.round((foodBudget.spent / foodBudget.limit) * 100)}% of your budget used with about 8 days left in the month.\n\nYour top food expenses were at Whole Foods ($87.43), Nobu Restaurant ($210.00), and Costco ($156.80). Consider meal prepping to reduce dining out costs — you could save up to $150/month!`,
      richData: {
        type: 'budget_breakdown',
        payload: { categories: budgetCategories.slice(0, 4) },
      },
    }
  }

  if (q.includes('goal') || q.includes('saving') || q.includes('savings')) {
    const totalTarget = goals.reduce((s, g) => s + g.targetAmount, 0)
    const totalCurrent = goals.reduce((s, g) => s + g.currentAmount, 0)
    const pct = Math.round((totalCurrent / totalTarget) * 100)
    return {
      text: `You're making great progress on your financial goals! Overall you're **${pct}% of the way** to your combined targets.\n\n🛡️ **Emergency Fund** is your most urgent — you're 65% there and on track to complete it by June 2026 at your current $500/month contribution.\n\n✈️ **Japan Vacation** needs a bit more attention — consider bumping contributions by $100/month to hit your December deadline.`,
      richData: { type: 'goal_progress', payload: { goals } },
    }
  }

  if (
    q.includes('invest') ||
    q.includes('portfolio') ||
    q.includes('stock') ||
    q.includes('crypto')
  ) {
    const totalValue = investments.reduce((s, i) => s + i.value, 0)
    const totalChange = investments.reduce((s, i) => s + i.change * i.shares, 0)
    return {
      text: `Your investment portfolio is worth **$${totalValue.toLocaleString()}** today, up **$${totalChange.toFixed(2)}** (+1.2%) from yesterday.\n\nYour biggest winner today is **GOOGL** (+2.0%) and **AAPL** (+1.25%). Bitcoin is your largest holding at $34,447 (55.8% of portfolio).\n\n💡 **Insight**: Your crypto allocation is quite high at ~68%. Consider rebalancing to reduce volatility risk.`,
      richData: {
        type: 'investment_summary',
        payload: { investments: investments.slice(0, 5) },
      },
    }
  }

  if (
    q.includes('transaction') ||
    q.includes('recent') ||
    q.includes('purchase') ||
    q.includes('spent')
  ) {
    return {
      text: `Here are your most recent transactions. Your biggest expense this week was at **Whole Foods** ($87.43). You've had **${transactions.filter((t) => t.type === 'debit').length}** debit transactions this month totaling **$5,317.43**.`,
      richData: {
        type: 'transaction_list',
        payload: { transactions: transactions.slice(0, 6) },
      },
    }
  }

  if (
    q.includes('budget') ||
    q.includes('overspend') ||
    q.includes('over budget')
  ) {
    const overBudget = budgetCategories.filter((b) => b.spent > b.limit)
    return {
      text: `Here's your budget overview for February 2026. You're over budget in **${overBudget.length} categories**: ${overBudget.map((b) => b.name).join(', ')}.\n\nYour **Shopping** category is the biggest concern — you've spent $841.93 against a $800 budget. Consider pausing non-essential purchases for the rest of the month.\n\n✅ Great news: Entertainment and Transport are well under budget!`,
      richData: {
        type: 'budget_breakdown',
        payload: { categories: budgetCategories },
      },
    }
  }

  if (q.includes('credit') || q.includes('score')) {
    return {
      text: `Your current credit score is **742** (Very Good). 📈 You've improved by **44 points** over the past year — excellent progress!\n\nKey factors:\n• ✅ Payment history: 98% on-time payments\n• ⚠️ Credit utilization: 28% (keep below 30%)\n• 📅 Average account age: 4.2 years\n\n💡 **Tip**: Paying down your credit card balance by $500 could boost your score by 8-12 points.`,
      richData: {
        type: 'insight_card',
        payload: {
          score: 742,
          change: 44,
          tip: 'Pay down $500 on your credit card to boost score by 8-12 points',
        },
      },
    }
  }

  if (q.includes('net worth') || q.includes('wealth')) {
    return {
      text: `Your current net worth is **$76,420** — up **$7,620 (11.1%)** over the past 6 months. 🎉\n\nBreakdown:\n• 💰 Cash & Savings: $14,621\n• 📈 Investments: $61,799\n• 🏠 Assets: $0 (no real estate yet)\n• 💳 Liabilities: -$0 (debt-free!)\n\nYou're building wealth faster than 78% of people your age. Keep it up!`,
      richData: {
        type: 'insight_card',
        payload: { netWorth: 76420, change: 7620, changePercent: 11.1 },
      },
    }
  }

  if (
    q.includes('reduce') ||
    q.includes('save more') ||
    q.includes('tip') ||
    q.includes('advice')
  ) {
    return {
      text: `Here are my top 3 personalized recommendations to improve your finances:\n\n**1. 🍽️ Reduce dining out** — You spent $210 at Nobu this month. Cooking at home 2 more nights/week could save ~$200/month.\n\n**2. 📦 Review subscriptions** — You're paying for Netflix, Spotify, Disney+, Hulu, and Apple. That's $62.95/month. Consider consolidating to 2-3 services.\n\n**3. 🏦 Boost emergency fund** — You're 65% to your goal. Increasing monthly contribution by $200 gets you there 2 months earlier.`,
      richData: { type: 'insight_card', payload: { tips: true } },
    }
  }

  // Default response
  return {
    text: `I'm your personal finance AI assistant! I can help you with:\n\n• 📊 **Spending analysis** — "How much did I spend on food?"\n• 🎯 **Goal tracking** — "Am I on track with my savings?"\n• 📈 **Investment insights** — "Analyze my portfolio"\n• 💳 **Credit monitoring** — "What's my credit score?"\n• 💡 **Money tips** — "How can I save more?"\n\nWhat would you like to explore?`,
  }
}

export function useChat() {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '0',
      role: 'assistant',
      content:
        "Hi Alex! 👋 I'm your FinanceAI assistant. I can help you track spending, analyze your budget, understand your investments, and reach your financial goals. What would you like to know?",
      timestamp: new Date(),
    },
  ])
  const [isLoading, setIsLoading] = useState(false)
  const streamingRef = useRef<NodeJS.Timeout | null>(null)

  const sendMessage = useCallback(
    (content: string) => {
      if (!content.trim() || isLoading) return

      const userMsg: Message = {
        id: Date.now().toString(),
        role: 'user',
        content: content.trim(),
        timestamp: new Date(),
      }

      setMessages((prev) => [...prev, userMsg])
      setIsLoading(true)

      // Simulate SSE delay
      setTimeout(() => {
        const { text, richData } = generateResponse(content)
        const words = text.split(' ')
        const assistantId = (Date.now() + 1).toString()

        const assistantMsg: Message = {
          id: assistantId,
          role: 'assistant',
          content: '',
          richData,
          timestamp: new Date(),
          isStreaming: true,
        }

        setMessages((prev) => [...prev, assistantMsg])

        let wordIndex = 0
        const streamWords = () => {
          if (wordIndex < words.length) {
            const chunk = words.slice(0, wordIndex + 1).join(' ')
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantId ? { ...m, content: chunk } : m,
              ),
            )
            wordIndex++
            streamingRef.current = setTimeout(
              streamWords,
              40 + Math.random() * 30,
            )
          } else {
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantId ? { ...m, isStreaming: false } : m,
              ),
            )
            setIsLoading(false)
          }
        }
        streamWords()
      }, 600)
    },
    [isLoading],
  )

  const clearMessages = useCallback(() => {
    setMessages([
      {
        id: '0',
        role: 'assistant',
        content:
          "Hi Alex! 👋 I'm your FinanceAI assistant. How can I help you today?",
        timestamp: new Date(),
      },
    ])
  }, [])

  return {
    messages,
    sendMessage,
    isLoading,
    suggestedPrompts: SUGGESTED_PROMPTS,
    clearMessages,
  }
}
